import { useCallback, useEffect, useState, type ReactNode } from "react";

import { money } from "../money.ts";
import type {
  Availability,
  Catalog,
  Merchant,
  MerchantLocation,
  Product,
  RiderApi,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  AddControl,
  Group,
  InfoNote,
  MarketShell,
  PillControl,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Products · stock` — the one screen of the commerce set the measurement grades **buildable**, and
 * the only one of the thirteen that reads four services to draw one list.
 *
 * `docs/design/DESIGN_RECONCILIATION.md` got there first: _"`Products · stock` needs COM-01, COM-02
 * and COM-03 where the file cites COM-03 alone; the price and the category name are the other two's,
 * and both are published."_ MER-01 and MER-02 are the fourth and fifth, for the app bar's two lines.
 *
 * ## Stock, four ways, derived rather than published
 *
 * The design's own note, and the whole point of the screen: _"In stock (with age), last known (stale
 * with a fallback), not reported (missing with a fallback), and out of stock. Each has its own glyph
 * and word."_ Its next sentence is the gap: _"Contract gap G-36: COM-03 answers only
 * availableQuantity and updatedAt. There is no freshness or fallback field, so the four states are
 * derived from age and absence until the service publishes them."_
 *
 * `docs/design/DESIGN_RECONCILIATION.md` confirms G-36 is **still open** and names the pattern to
 * copy: _"DRV-05's `fresh` is the pattern — the service judges it, the screen does not."_ So the
 * derivation below is a stand-in for a judgement that belongs upstream, and the one number it needs
 * — how old a count has to be to be stale — is this build's and is named in one place rather than
 * written at a call site.
 *
 * | Drawn                                       | Derived from                                      |
 * | ------------------------------------------- | ------------------------------------------------- |
 * | `in stock · updated 2 min ago`              | `availableQuantity > 0`, `updatedAt` inside the window |
 * | `last known 40 min ago`                     | `availableQuantity > 0`, `updatedAt` outside it   |
 * | `stock not reported`                        | COM-03 answered nothing for this SKU              |
 * | `out of stock`                              | `availableQuantity === 0`                         |
 *
 * The design's third row writes `stock not reported · usually in`. The second half of that is a
 * **fallback claim** — that the store usually has it — and nothing publishes one, so it is dropped
 * rather than asserted. That is the "fallback" half of G-36.
 *
 * ## One request per row, bounded
 *
 * COM-03 is addressed by a SKU and publishes no listing, so a page of eight products is eight reads
 * from a phone. EXP-002's `cart-reviews` bounds exactly this sweep for a cart and publishes how far
 * it reached; nothing composes a product list, so this screen bounds it itself at `STOCK_LOOKUPS`
 * and says in the row's own badge which rows were never asked about — rather than drawing them as
 * though the store had answered.
 */

export interface ProductsProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  /** Every identifier this screen is about, because none of them is discoverable. See below. */
  readonly merchantId: string | undefined;
  readonly locationId: string | undefined;
  readonly catalogId: string | undefined;
  readonly categoryId: string | undefined;
  readonly onBack: () => void;
}

/**
 * How old a count may be before it is drawn as `last known` rather than `in stock`.
 *
 * Fifteen minutes. **This build's number, not the platform's**: COM-03 publishes `updatedAt` and no
 * freshness window, which is G-36. Named here so it can be found and changed in one place, and so
 * the day the service publishes a judgement this constant is what gets deleted. The design's own two
 * samples are `updated 2 min ago` (fresh) and `last known 40 min ago` (stale), so any boundary
 * between those two draws the design's own rows correctly — which is exactly why a boundary chosen
 * that way is not evidence of anything and is recorded as a question.
 */
const FRESH_WINDOW_MS = 15 * 60 * 1000;

/**
 * How many rows this screen will ask COM-03 about.
 *
 * Twelve, which is EXP-002's own `CART_ITEM_LOOKUPS` order of magnitude for the same sweep over the
 * same service. A thirteenth row is drawn with its stock never asked for, said in its badge.
 */
const STOCK_LOOKUPS = 12;

/** The design's glyph for an item of no published category — `CLAUDE.md` section 3.5's own. */
const PRODUCT_GLYPH = "package_2";

/**
 * Why neither `add` nor `View cart` can be pressed.
 *
 * **COM-04 publishes no way to find a customer's current cart.** Its four operations are
 * `POST /v1/carts`, `GET /v1/carts/{cartId}`, `PUT /v1/carts/{cartId}/items` and
 * `DELETE /v1/carts/{cartId}/items/{productId}` — a create, and three reads and writes by an
 * identifier. There is no listing and no "the cart of this customer", so an `add` pressed on a fresh
 * arrival would have to `POST /v1/carts` and would mint **a new cart on every press**: a rider
 * tapping three items would have three carts and could see none of them.
 */
const NO_CART =
  "COM-04 publishes no listing of this customer's carts — only POST /v1/carts, which would mint a new one on every press";

interface Loaded {
  readonly merchant: Merchant | undefined;
  readonly location: MerchantLocation | undefined;
  readonly catalog: Catalog | undefined;
  readonly products: readonly Product[];
  /** One entry per SKU COM-03 answered for. A SKU absent here was asked and not answered, or
   * was past `STOCK_LOOKUPS` and never asked — `asked` says which. */
  readonly stock: ReadonlyMap<string, Availability>;
  readonly asked: ReadonlySet<string>;
}

export function Products(props: ProductsProps): ReactNode {
  const { api, tenantId, merchantId, locationId, catalogId, categoryId } = props;

  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });

    /*
     * The two app-bar reads and the catalogue are `catch`-to-undefined rather than fatal.
     *
     * A merchant name this screen could not read is a missing app-bar line; a product list it could
     * not read is no screen at all. Those are different failures and the rules say to draw each as
     * itself — so only COM-02's listing decides whether this screen is an error state.
     */
    const merchant =
      merchantId === undefined
        ? Promise.resolve(undefined)
        : api.merchant(merchantId).catch(() => undefined);
    const location =
      locationId === undefined
        ? Promise.resolve(undefined)
        : api.merchantLocation(locationId).catch(() => undefined);
    const catalog =
      catalogId === undefined
        ? Promise.resolve(undefined)
        : api.catalog({ tenantId, catalogId }).catch(() => undefined);

    Promise.all([
      merchant,
      location,
      catalog,
      api.products({
        tenantId,
        ...(catalogId === undefined ? {} : { catalogId }),
        ...(categoryId === undefined ? {} : { categoryId }),
        ...(merchantId === undefined ? {} : { merchantId }),
      }),
    ])
      .then(async ([foundMerchant, foundLocation, foundCatalog, products]) => {
        const stock = new Map<string, Availability>();
        const asked = new Set<string>();
        if (merchantId !== undefined && locationId !== undefined) {
          for (const product of products.slice(0, STOCK_LOOKUPS)) {
            asked.add(product.sku);
            try {
              stock.set(
                product.sku,
                await api.availability({ merchantId, locationId, sku: product.sku }),
              );
            } catch {
              /*
               * Swallowed per SKU, deliberately.
               *
               * A 404 from COM-03 is the answer `nobody has told the store's system about this
               * item`, which is one of the design's four states — and a refusal or an outage on one
               * SKU must not take the other eleven rows down with it. EXP-002 makes the same
               * distinction for the cart and publishes it as `stockAbsence`; here the absence is
               * the SKU not being in the map, and `asked` is what separates "we asked and got
               * nothing" from "we never asked".
               */
            }
          }
        }
        setLoaded({
          merchant: foundMerchant,
          location: foundLocation,
          catalog: foundCatalog,
          products,
          stock,
          asked,
        });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) =>
        setState(
          stateForError(error, [
            SCOPES.commerceProducts,
            SCOPES.catalog,
            SCOPES.inventory,
            SCOPES.merchants,
          ]),
        ),
      );
  }, [api, tenantId, merchantId, locationId, catalogId, categoryId]);

  useEffect(load, [load]);

  if (state.kind !== "ready" || loaded === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading what this shop has."
        barTitle="Market"
        barSub="CityCab"
      />
    );
  }

  const rows: RowValue[] = loaded.products.map((product) => ({
    id: product.productId,
    lead: PRODUCT_GLYPH,
    title: product.name,
    /*
     * The design's `each`, `bunch`, `tray` — a unit of measure, which COM-02 does not publish.
     *
     * `description` is the one published field that belongs on a product's second line, so it is
     * what is drawn. A product whose description is empty gets the absence rather than a blank row.
     */
    ...(product.description.length > 0
      ? { sub: product.description }
      : { subAbsent: "No unit or description is published" }),
    amount: money(product.price),
    badge: stockBadge(loaded, product),
    control: stockControl(loaded, product),
    dimmed: loaded.stock.get(product.sku)?.availableQuantity === 0,
  }));

  return (
    <MarketShell
      screen="products"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle={loaded.merchant?.tradingName ?? "This shop"}
      barSub={
        loaded.location === undefined ? (
          <Absent words="No branch: MER-02 publishes no listing, so none is addressable" />
        ) : (
          `${String(loaded.location.preparationMinutes)} min`
        )
      }
      right={{
        glyph: "help",
        label: "Help",
        unavailable: "No design file draws a help sheet for this screen",
      }}
      badge={branchBadge(loaded)}
      title={categoryName(loaded, categoryId)}
      sub="Stock is shown the way the store reports it."
      actions={[
        {
          /*
           * `View cart · 3 items` in the design, and `View cart` here.
           *
           * The count is the number of lines in **this customer's cart**, and COM-04 publishes no
           * way to find one: a create, and three operations by an identifier. So the figure is
           * dropped rather than invented, and the button says the same thing its `add` siblings do.
           */
          label: "View cart",
          glyph: "shopping_basket",
          primary: true,
          unavailable: NO_CART,
        },
      ]}
    >
      <Group rows={rows} empty={<EmptyShelf />} />
      <InfoNote text="Items without a live count can still be ordered. The store confirms them, and you only pay for what's packed." />
    </MarketShell>
  );
}

/** The chip: the branch's name and whether it is trading, both from MER-02. */
function branchBadge(loaded: Loaded): BadgeValue {
  if (loaded.location === undefined) {
    return { kind: "unknown", label: "branch not addressable" };
  }
  return {
    kind: loaded.location.status === "active" ? "active" : "closed",
    label: loaded.location.name,
  };
}

/**
 * `Fruit and veg` — the category this list is of, from COM-01's own `categories`.
 *
 * COM-01 publishes `catalog.categories` as an array of objects and the generator flattens two
 * levels, so the member names are read defensively: a category whose shape is not `{categoryId,
 * name}` leaves the heading absent rather than printing an identifier where a name belongs.
 */
function categoryName(loaded: Loaded, categoryId: string | undefined): string {
  const categories = loaded.catalog?.categories ?? [];
  const found =
    categoryId === undefined
      ? categories[0]
      : categories.find((category) => category.categoryId === categoryId);
  return found?.name ?? loaded.catalog?.name ?? "This shelf";
}

/**
 * The four stock states, derived. G-36.
 *
 * The order of the tests is the order the design lists them in, and the last one is the one that
 * matters: a SKU this screen never asked about is **not** drawn as `stock not reported`, because
 * "the store said nothing" and "nobody asked the store" draw the same empty shelf and only one of
 * them is a fact about the shop. EXP-002's `inStock` is three-valued for exactly this reason.
 */
function stockBadge(loaded: Loaded, product: Product): BadgeValue {
  if (!loaded.asked.has(product.sku)) {
    return { kind: "unknown", label: `stock not asked for · over ${String(STOCK_LOOKUPS)} rows` };
  }
  const availability = loaded.stock.get(product.sku);
  if (availability === undefined) return { kind: "unknown", label: "stock not reported" };
  if (availability.availableQuantity === 0) return { kind: "closed", label: "out of stock" };
  const age = Date.now() - Date.parse(availability.updatedAt);
  return age <= FRESH_WINDOW_MS
    ? { kind: "done", label: `in stock · updated ${ago(age)}` }
    : { kind: "caution", label: `last known ${ago(age)}` };
}

/** `updated 2 min ago` — the design's own phrasing for an age, in whole minutes. */
function ago(milliseconds: number): string {
  const minutes = Math.max(0, Math.round(milliseconds / 60_000));
  if (minutes === 0) return "just now";
  if (minutes === 1) return "1 min ago";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "1 hr ago" : `${String(hours)} hr ago`;
}

/**
 * `add` on a row that can be bought, `Notify me` on one that cannot, and neither can be pressed.
 *
 * **COM-04 publishes no way to find a customer's current cart.** Its four operations are
 * `POST /v1/carts`, `GET /v1/carts/{cartId}`, `PUT /v1/carts/{cartId}/items` and
 * `DELETE /v1/carts/{cartId}/items/{productId}` — a create, and three reads and writes by an
 * identifier. There is no listing and no "the cart of this customer", so an `add` pressed on a fresh
 * arrival would have to `POST /v1/carts` and would mint **a new cart on every press**: a rider
 * tapping three items would have three carts and could see none of them. So the control is drawn,
 * because the design draws it, and pressing it says that instead.
 *
 * `Notify me` has no route at all: nothing on this platform records an interest in a SKU coming back
 * into stock.
 */
function stockControl(loaded: Loaded, product: Product): ReactNode {
  const out = loaded.stock.get(product.sku)?.availableQuantity === 0;
  return out ? (
    <PillControl
      label="Notify me"
      unavailable="Nothing on this platform records an interest in a SKU coming back into stock"
    />
  ) : (
    <AddControl label={`Add ${product.name}`} unavailable={NO_CART} />
  );
}

/**
 * The empty state, with the words this build's.
 *
 * A category COM-02 answers nothing for is an empty shelf and is said as one. It is not the same
 * thing as a category whose stock was not asked about, which is drawn per row above.
 */
function EmptyShelf(): ReactNode {
  return (
    <div className="row">
      <span className="row__middle">
        <span className="row__title">Nothing on this shelf</span>
        <span className="row__sub">This shop has no products listed in this category.</span>
      </span>
    </div>
  );
}
